import { IState } from '@/store/types';
import { getProductByName } from '@/helpers/products';
import { IStripeSubscription, IStripeSubscriptionItem } from '@/store/stripe-types';

const bundlePrices = ['churnkey_platform_starter', 'churnkey_platform_scale', 'churnkey_platform_enterprise'];

export const hasProductSubscription = (state: IState, productName: string) => {
  const product = getProductByName(state, productName);
  if (!product) {
    return false;
  }

  // with bundle-pricing, stripe products don't match the stripeProductIds since we are still using the old stripe products
  // to control access, even though they've purchased a new platform product bundle (e.g. product.stripeProductId === 'churnkey_platform_starter')
  // so we can't look for the individual product in the stripe subscription
  return hasNonTrialProductSubscription(state, productName) || hasTrialSubscription(state, productName); 
};

export const hasNonTrialProductSubscription = (state: IState, productName: string) => {
  const product = getProductByName(state, productName);

  if (!product) {
    return false;
  }

  // TEMPORARY FIX: If org has one of the bundle products, then just return true
  if (hasAnyBundleProduct(state)) {
    return true;
  }

  return (
    state.org?.stripe?.subscriptions?.data?.some((dataItem) => dataItem.items.data.some((item) => item.plan.product === product.stripeProductId)) &&
    product.isTrial !== true
  );
};

export const hasTrialSubscription = (state: IState, productName: string) => {
  const product = getProductByName(state, productName);
  if (!product) {
    return false;
  }
  
  // Bundle pricing
  if (state.org?.bundlePricing) {
    const hasTrialStatus = state.org?.stripe?.subscriptions?.data?.some((sub) => 
      sub.status === 'trialing'
    );
    return hasTrialStatus;
  }
  
  // For non-bundle customers (2nd system): Check subscription status
  // First check if any subscription is in 'trialing' status
  const hasTrialingStatus = state.org?.stripe?.subscriptions?.data?.some((sub) => 
    sub.status === 'trialing'
  );
  
  if (hasTrialingStatus) {
    return true;
  }
  
  // If no trialing status, check if they're on a trial price plan for this specific product
  // This checks if any subscription item has this product and is using a trial price
  const hasTrialPricePlan = state.org?.stripe?.subscriptions?.data?.some((subscription) => 
    subscription.items?.data?.some((item) => {
      // Check if this item is for our product
      if (item.plan?.product !== product.stripeProductId && item.price?.product !== product.stripeProductId) {
        return false;
      }
      
      // Check if they're on a trial price plan (if product has trial price defined)
      if (product.trialPrice?.id) {
        return item.plan?.id === product.trialPrice.id || item.price?.id === product.trialPrice.id;
      }
      
      return false;
    })
  );
  
  return hasTrialPricePlan || false;
};

export const hasNonMonthlySubscription = (state: IState) => {
  const subscriptions = state.org?.stripe?.subscriptions?.data;
  if (!subscriptions) {
    return false;
  }
  return subscriptions.some((subscription: IStripeSubscription) => {
    return subscription.items.data.some((item: IStripeSubscriptionItem) => {
      const isNotMonthly = item.plan.interval !== 'month';
      const isNotSingleInterval = item.plan.interval_count !== 1;
      return isNotMonthly || isNotSingleInterval;
    });
  });
};

export const hasChurnkeyStarterPlan = (state: IState) => {
	return state.org?.stripe?.subscriptions?.data?.some((dataItem) =>
		dataItem.items.data.some(
			(item) => item.plan.product === "churnkey_platform_starter",
		),
	);
};

export const hasAnyBundleProduct = (state: IState) => {
  const subscriptions = state.org?.stripe?.subscriptions?.data ?? [];
  return subscriptions.some((subscription) => {
    return subscription.items.data.some((item) => {
      return bundlePrices.includes(item.plan.product);
    });
  });
};